import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import {
  useActivateKPI,
  useCopyKPI,
  useDeactivateKPI,
  useDeleteKPI,
  useKPIsQuery,
} from "../api/queries";
import { KPITour, useKPITour } from "../tour/KPITour";
import { KPIFilter } from "../ui/KPIFilter";
import { KPITable } from "../ui/KPITable";
import { KPIToolbar } from "../ui/KPIToolbar";

export function KPIsPage() {
  const nav = useNavigate({ from: "/app/kpis" });
  const search = useSearch({ from: "/app/kpis" }) as OffsetPageQueryDTO;
  const tour = useKPITour();

  const list = useKPIsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canCreate = useActionPermission(["kpi:create"]);
  const canManage = useActionPermission(["kpi:update", "kpi:delete"]);

  const deleteKPI = useDeleteKPI();
  const copyKPI = useCopyKPI();
  const activateKPI = useActivateKPI();
  const deactivateKPI = useDeactivateKPI();

  return (
    <>
      <Header />

      <Main>
        <KPITour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <KPIToolbar canCreate={!!canCreate} onStartTour={tour.startTour} />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <KPIFilter />

          <KPITable
            canManage={!!canManage}
            canCreate={!!canCreate}
            isLoading={list.isLoading}
            data={list.data?.data ?? []}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(kpi) => nav({ to: `/app/kpi/${kpi.id}/edit` })}
            onCopy={async (id) => {
              await toast.promise(copyKPI.mutateAsync(id), {
                loading: "ກຳລັງຄັດລອກ...",
                success: "ຄັດລອກ KPI ສຳເລັດ",
                error: "ຄັດລອກ KPI ລົ້ມເຫຼວ",
              });
            }}
            onActivate={async (id) => {
              await toast.promise(activateKPI.mutateAsync(id), {
                loading: "ກຳລັງເປີດການໃຊ້ງານ...",
                success: "ເປີດການໃຊ້ງານ KPI ສຳເລັດ",
                error: "ເປີດການໃຊ້ງານ KPI ລົ້ມເຫຼວ",
              });
            }}
            onDeactivate={async (id) => {
              await toast.promise(deactivateKPI.mutateAsync(id), {
                loading: "ກຳລັງປິດການໃຊ້ງານ...",
                success: "ປິດການໃຊ້ງານ KPI ສຳເລັດ",
                error: "ປິດການໃຊ້ງານ KPI ລົ້ມເຫຼວ",
              });
            }}
            onDelete={async (id) => {
              await toast.promise(deleteKPI.mutateAsync(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບ KPI ສຳເລັດ",
                error: "ລຶບ KPI ລົ້ມເຫຼວ",
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
